// @ts-ignore

import { useGlobalStore } from "controller/store/globalStore";
import type { MqttClient } from "mqtt";
// @ts-ignore
import mqttjs from "mqtt/dist/mqtt";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  debugConsole,
  getOrCreateDeviceId,
  getOrCreateTabId,
} from "utilz/helpers";
import type { MqttMessage } from "./MqttCreateContext";
import { MqttContext } from "./MqttCreateContext";

const { Provider } = MqttContext;

// Stable per-user/per-device/per-tab clientId. clean: false only resumes a
// session when the clientId is the same across reconnects, so it must NOT be
// random per session. resourceId may be null on first login (the provider
// mounts above auth) — we connect as "anon" and reconnect once it's known.
const buildClientId = () => {
  const resourceId = useGlobalStore.getState().resourceId ?? "anon";
  return `sd-${resourceId}-${getOrCreateDeviceId()}-${getOrCreateTabId()}`;
};

const MqttProvider = ({ children }: { children: React.ReactNode }) => {
  const [mqttMessage, setMqttMessage] = useState<MqttMessage | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Use ref for the client so state changes don't cascade re-renders
  const clientRef = useRef<MqttClient | null>(null);
  const isConnectingRef = useRef(false);

  // Subscription reference counting — only unsubscribe when count reaches 0
  const subCountRef = useRef<Map<string, number>>(new Map());

  // Refetch callback registered by a consumer inside QueryClientProvider.
  // Invoked when the broker reports a non-resumed session so missed messages
  // can be recovered (see setRefetchHandler in the context value).
  const refetchHandlerRef = useRef<(() => void) | null>(null);

  // The clientId the active client connected with. Used to detect when we
  // connected as "anon" (before auth) and need to reconnect once the user id
  // is known so the broker session becomes per-user.
  const connectedClientIdRef = useRef<string | null>(null);

  const setRefetchHandler = useCallback((handler: (() => void) | null) => {
    refetchHandlerRef.current = handler;
  }, []);

  const connectMqtt = useCallback(() => {
    if (isConnectingRef.current || clientRef.current?.connected) {
      debugConsole("Connection already in progress or established, skipping");
      return;
    }

    // A stale client may still exist from a previous mount (StrictMode
    // double-invokes effects) or a reconnect that hasn't fully closed. Opening
    // a second client with the same clientId makes the broker kick one of them,
    // and MQTT.js auto-reconnects it — an endless connect/disconnect flap.
    // Force-close any lingering client before opening a new one.
    if (clientRef.current) {
      clientRef.current.removeAllListeners?.();
      clientRef.current.end(true);
      clientRef.current = null;
    }

    isConnectingRef.current = true;
    debugConsole("Connecting ...");

    const clientId = buildClientId();
    connectedClientIdRef.current = clientId;

    const newClient = mqttjs.connect(
      `wss://${import.meta.env.VITE_APP_MQTT_URL}`,
      {
        clientId,
        host: `${import.meta.env.VITE_APP_MQTT_URL}`,
        protocol: "wss",
        protocolVersion: 5,
        username: `${import.meta.env.VITE_APP_MQTT_USERNAME}`,
        password: `${import.meta.env.VITE_APP_MQTT_PASSWORD}`,
        // Durable session: the broker keeps our subscriptions and queues
        // QoS>=1 messages while we're briefly disconnected, then replays them
        // on reconnect instead of dropping them. Requires a stable clientId.
        clean: false,
        properties: { sessionExpiryInterval: 86400 },
        port: 8083,
        path: "/mqtt",
        reconnectPeriod: 5000,
        connectTimeout: 10000,
      },
    ) as MqttClient;

    clientRef.current = newClient;

    newClient.on("connect", (connack: { sessionPresent?: boolean }) => {
      isConnectingRef.current = false;
      // Reset ref counts so consumers re-subscribe at the protocol level.
      // The first consumer effect will re-issue SUBSCRIBE after this.
      subCountRef.current.clear();
      setIsConnected(true);
      debugConsole("connection successful");

      // sessionPresent === false means the broker had no saved session for us
      // (first connect, or it expired) — anything published while we were away
      // is gone, so trigger a refetch to recover missed messages.
      if (!connack?.sessionPresent) {
        debugConsole("non-resumed session — triggering refetch");
        refetchHandlerRef.current?.();
      }
    });

    newClient.on("error", (err: Error) => {
      isConnectingRef.current = false;
      debugConsole("Connection error: ", err);
      // Don't destroy the client here — let reconnectPeriod handle recovery.
      // The MQTT.js library auto-reconnects; calling client.end() kills that.
    });

    newClient.on("reconnect", () => {
      debugConsole("reconnecting");
    });

    newClient.on("close", () => {
      setIsConnected(false);
      debugConsole("Disconnected");
    });

    newClient.on("message", (topic: string, payload: Buffer) => {
      const message = payload.toString();
      // flushSync forces React to process each state update synchronously,
      // preventing message loss when multiple MQTT messages arrive in the
      // same tick (e.g. bulk member_added events).
      flushSync(() => {
        setMqttMessage({ topic, message });
      });
      debugConsole(`received message: ${message} from topic: ${topic}`);
    });
  }, []);

  // Connect once on mount, clean up on unmount
  useEffect(() => {
    connectMqtt();

    return () => {
      if (clientRef.current) {
        // Remove listeners first so the dying client's auto-reconnect/close
        // events don't fire against an unmounted provider, then force-close
        // (true) so the socket dies immediately instead of lingering and
        // colliding with the next mount's connection on the same clientId.
        clientRef.current.removeAllListeners?.();
        clientRef.current.end(true);
        clientRef.current = null;
      }
      isConnectingRef.current = false;
      subCountRef.current.clear();
    };
  }, [connectMqtt]);

  // The provider mounts above auth, so the first connect may use an "anon"
  // clientId. Once resourceId becomes known, reconnect once with the per-user
  // clientId so the broker session is scoped to the user. Subscribing to the
  // store directly (not via a selector hook) avoids re-rendering the provider.
  const resourceId = useGlobalStore((state) => state.resourceId);
  useEffect(() => {
    if (!resourceId) return;
    // Already connected as this user — nothing to do.
    if (connectedClientIdRef.current?.startsWith(`sd-${resourceId}-`)) return;
    // Only force a reconnect if we're currently on the anon session.
    if (!connectedClientIdRef.current?.startsWith("sd-anon-")) return;

    debugConsole("resourceId known — reconnecting with per-user clientId");
    if (clientRef.current) {
      clientRef.current.removeAllListeners?.();
      clientRef.current.end(true);
      clientRef.current = null;
    }
    isConnectingRef.current = false;
    subCountRef.current.clear();
    connectMqtt();
  }, [resourceId, connectMqtt]);

  // Stable subscribe — ref-counted so multiple consumers on the same topic
  // don't stomp each other's subscriptions
  const subscribeRoom = useCallback(
    (roomId: string | string[]) => {
      const client = clientRef.current;
      const topics = Array.isArray(roomId) ? roomId : [roomId];

      for (const t of topics) {
        const count = subCountRef.current.get(t) ?? 0;
        subCountRef.current.set(t, count + 1);

        // Only actually subscribe on the first consumer.
        // QoS 1 is required for clean: false to do anything — the broker only
        // queues messages for an offline session at QoS >= 1.
        if (count === 0) {
          debugConsole(t);
          client?.subscribe?.(t, { qos: 1 }, (error: Error) => {
            if (error) {
              debugConsole("Subscribe to topics error", error);
              return;
            }
            debugConsole(`Subscribe to topics: ${t}`);
          });
        }
      }
    },
    [], // stable — accesses client via ref
  );

  // Stable unsubscribe — only sends unsubscribe when ref count hits 0
  const unSubscribeRoom = useCallback(
    (roomId: string | string[]) => {
      const client = clientRef.current;
      const topics = Array.isArray(roomId) ? roomId : [roomId];

      for (const t of topics) {
        const count = subCountRef.current.get(t) ?? 0;
        if (count <= 1) {
          subCountRef.current.delete(t);
          client?.unsubscribe?.(t, (error: unknown) => {
            if (error) {
              debugConsole("UnSubscribe to topics error", error);
            }
          });
        } else {
          subCountRef.current.set(t, count - 1);
        }
      }
    },
    [], // stable — accesses client via ref
  );

  return (
    <Provider
      value={{
        mqttMessage,
        subscribeRoom,
        unSubscribeRoom,
        isConnected,
        setIsConnected,
        setRefetchHandler,
      }}
    >
      {children}
    </Provider>
  );
};
export { MqttContext, MqttProvider };
